import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";

const authStatusForWhichInputIsAllowed = [
  MedicalPaOrdersAuthStatus.AuthByRISA,
  MedicalPaOrdersAuthStatus.AuthOnFile,
  MedicalPaOrdersAuthStatus.NoAuthRequired,
  MedicalPaOrdersAuthStatus.AuthRequired,
  MedicalPaOrdersAuthStatus.Pending,
  MedicalPaOrdersAuthStatus.DeniedByRISA,
  MedicalPaOrdersAuthStatus.DenialByRISA,
  MedicalPaOrdersAuthStatus.ExistingDenial,
];

const authStatusForWhichAuthNumberIsAllowed = [
  MedicalPaOrdersAuthStatus.AuthByRISA,
  MedicalPaOrdersAuthStatus.AuthOnFile,
  MedicalPaOrdersAuthStatus.NoAuthRequired,
  MedicalPaOrdersAuthStatus.Pending,
  MedicalPaOrdersAuthStatus.DeniedByRISA,
  MedicalPaOrdersAuthStatus.DenialByRISA,
  MedicalPaOrdersAuthStatus.ExistingDenial,
  MedicalPaOrdersAuthStatus.DenialAfterQuery,
];

const authStatusForWhichPendingNumberIsVisible = [
  MedicalPaOrdersAuthStatus.Pending,
];

/**
 * Auth statuses for which an empty Auth# is allowed (no red border when blank).
 */
export const AUTH_STATUSES_ALLOWING_EMPTY_AUTH_NUMBER: MedicalPaOrdersAuthStatus[] =
  [MedicalPaOrdersAuthStatus.NoAuthRequired, MedicalPaOrdersAuthStatus.Pending];

/**
 * Returns whether the J-code cell should accept input for the given status.
 *
 * @param authStatus - Current PA-required status id.
 * @param jCode - Current J-code / CPT code value.
 * @returns `true` when the cell should be editable.
 */
export const determineIfTheJCodeFieldShouldBeEditable = (
  authStatus: string,
  jCode: string,
) => {
  const determineIfJCodeRequiresInput = jCode.trim() === "";
  const isAuthStatusValid = authStatusForWhichInputIsAllowed.includes(
    authStatus as MedicalPaOrdersAuthStatus,
  );
  return determineIfJCodeRequiresInput && isAuthStatusValid;
};

/**
 * Returns whether the Auth# cell should accept input for the given status.
 *
 * @param authStatus - Current PA-required status id.
 * @returns `true` when the Auth# cell should be editable.
 */
export const determineIfTheAuthNumberFieldShouldBeEditable = (
  authStatus: string,
) => {
  const isAuthStatusValid = authStatusForWhichAuthNumberIsAllowed.includes(
    authStatus as MedicalPaOrdersAuthStatus,
  );
  return isAuthStatusValid;
};

/**
 * Returns whether the Pending# cell should show an input for the given status.
 *
 * @param authStatus - Current PA-required status id.
 * @returns `true` when the Pending# cell should be visible as an input.
 */
export const determineIfThePendingNumberFieldShouldBeVisible = (
  authStatus: string,
) => {
  const isAuthStatusValid = authStatusForWhichPendingNumberIsVisible.includes(
    authStatus as MedicalPaOrdersAuthStatus,
  );
  return isAuthStatusValid;
};

/**
 * Returns whether a blank Auth# is allowed for the given status (no red border).
 *
 * @param authStatus - Current PA-required status id.
 * @returns `true` when empty Auth# is permitted for the status.
 */
export const doesAuthStatusAllowEmptyAuthNumber = (
  authStatus: string | undefined,
): boolean =>
  AUTH_STATUSES_ALLOWING_EMPTY_AUTH_NUMBER.includes(
    authStatus as MedicalPaOrdersAuthStatus,
  );
